import { PASSWORD_MIN_LENGTH, registerSchema } from '@ifretes/shared'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Botao, Campo, Cartao, ErroApi, Mensagem } from '../../components/ui'
import { api, ApiError } from '../../lib/api'

/** Máscara de telefone brasileiro, aplicada só na exibição. */
function mascararTelefone(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/**
 * Força da senha — indicador informativo, não bloqueante.
 *
 * A regra que barra o cadastro é o schema compartilhado; esta barra existe para
 * orientar enquanto a pessoa digita, e por isso nunca impede o envio.
 */
function forcaSenha(s: string): {
  nivel: number
  texto: string
  /** Cor do rotulo — token de TEXTO, que precisa de 4.5:1. */
  cor: string
  /** Cor da barrinha — preenchimento, 3:1 basta. */
  barra: string
} {
  let n = 0
  if (s.length >= PASSWORD_MIN_LENGTH) n++
  if (s.length >= 14) n++
  if (/[a-z]/.test(s) && /[A-Z]/.test(s)) n++
  if (/\d/.test(s)) n++
  if (/[^A-Za-z0-9]/.test(s)) n++

  // Tokens `-em-soft` e nao `--danger`/`--success` crus: este rotulo tem 12px em
  // negrito, e no tema escuro `--danger` mede 4:1 e `--success` 4.03:1 sobre o
  // cartao — abaixo do 4.5:1 exigido. `--warning` passa (5.91:1) e por isso fica
  // como esta. As barrinhas ao lado usam as cores CHEIAS: ali e preenchimento,
  // nao texto, e 3:1 basta.
  if (!s) return { nivel: 0, texto: '', cor: 'var(--text-3)', barra: 'var(--divider)' }
  if (n <= 2) {
    // barra = preenchimento (3:1 basta); cor = texto (precisa de 4.5:1)
    return { nivel: n, texto: 'Fraca', cor: 'var(--danger-em-soft)', barra: 'var(--danger)' }
  }
  if (n === 3) {
    return { nivel: n, texto: 'Razoável', cor: 'var(--warning)', barra: 'var(--warning)' }
  }
  if (n === 4) {
    // barra = preenchimento
    return { nivel: n, texto: 'Boa', cor: 'var(--success-em-soft)', barra: 'var(--success)' }
  }
  // barra = preenchimento
  return { nivel: 5, texto: 'Forte', cor: 'var(--success-em-soft)', barra: 'var(--success)' }
}

export function CriarConta() {
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [senha, setSenha] = useState('')
  const [tipo, setTipo] = useState<'DRIVER' | 'SHIPPER'>('DRIVER')
  const [aceito, setAceito] = useState(false)

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<unknown>(null)
  const [campos, setCampos] = useState<Record<string, string[]>>({})
  const [pronto, setPronto] = useState(false)
  // Comeca `true` porque o caso normal e o e-mail sair; a resposta corrige
  // quando nao sai.
  const [emailEnviado, setEmailEnviado] = useState(true)

  const forca = forcaSenha(senha)

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setCampos({})

    const r = registerSchema.safeParse({
      name: nome,
      email,
      password: senha,
      phone: telefone,
      accountType: tipo,
      acceptedTerms: aceito,
    })

    if (!r.success) {
      const mapa: Record<string, string[]> = {}
      for (const i of r.error.issues) {
        const k = i.path.join('.') || '_'
        ;(mapa[k] ??= []).push(i.message)
      }
      setCampos(mapa)
      return
    }

    setEnviando(true)
    try {
      const resposta = await api.registrar(r.data)
      setEmailEnviado(resposta.emailEnviado !== false)
      setPronto(true)
    } catch (err) {
      if (err instanceof ApiError) setCampos(err.camposComErro)
      setErro(err)
    } finally {
      setEnviando(false)
    }
  }

  if (pronto) {
    return (
      <div className="pagina-centrada">
        <Cartao
          titulo={emailEnviado ? 'Confirme seu e-mail' : 'Conta criada'}
          descricao={
            emailEnviado
              ? 'Falta um passo para ativar sua conta.'
              : 'Falta ativar o acesso, e isso depende de nós.'
          }
        >
          {emailEnviado ? (
            <>
              <Mensagem tipo="success" titulo="Cadastro recebido">
                Enviamos um link de confirmação para <strong>{email}</strong>. O link vale por 24
                horas.
              </Mensagem>
              <p className="ifr-nota ifr-fraco ifr-mt">
                {/* O remetente REAL, o mesmo de SMTP_FROM. Esta linha dizia
                    `nao-responda@gplansis.com`, endereco que nao existe: quem
                    procurasse no spam por ele nao acharia a mensagem. */}
                Não recebeu? Verifique a caixa de spam. O remetente é{' '}
                <strong>contato@gplansis.com</strong>.
              </p>
            </>
          ) : (
            /* A conta EXISTE — a transacao foi confirmada. O que falhou foi a
               notificacao. Dizer "enviamos um link" aqui seria mentira, e
               mandaria a pessoa esperar por uma mensagem que nao vem. */
            <>
              <Mensagem tipo="warning" titulo="Sua conta foi criada, mas o e-mail não saiu">
                Registramos <strong>{email}</strong> com a senha que você escolheu. Só não
                conseguimos enviar o link de confirmação agora, e sem ele o acesso ainda não abre.
              </Mensagem>
              <p className="ifr-nota ifr-fraco ifr-mt">
                Não é preciso se cadastrar de novo — repetir vai dizer que o e-mail já está em uso.
                Peça a ativação ao administrador em{' '}
                <a href="mailto:contato@gplansis.com" className="ifr-elo">
                  contato@gplansis.com
                </a>
                , informando este endereço.
              </p>
            </>
          )}
          <div className="ifr-mt">
            <Link to="/entrar" className="br-button secondary block">
              Ir para o login
            </Link>
          </div>
        </Cartao>
      </div>
    )
  }

  return (
    <div className="pagina-centrada">
      <Cartao
        titulo="Criar conta no iFretes"
        descricao="Leva menos de um minuto. Os documentos ficam para depois."
        largo
      >
        <ErroApi erro={erro} />

        <form onSubmit={enviar} noValidate className="ifr-pilha">
          {/* A escolha do tipo define o papel base (DRIVER ou SHIPPER) e vem
              primeiro porque muda o significado de todo o resto do cadastro. */}
          <fieldset className="ifr-sem-moldura">
            <legend
              className="ifr-rotulo-campo"
            >
              Você quer <span aria-hidden="true">…</span>
            </legend>
            <div className="ifr-par">
              {(
                [
                  { v: 'DRIVER', t: 'Transportar cargas', d: 'Motorista ou transportadora', i: 'fa-truck' },
                  { v: 'SHIPPER', t: 'Enviar cargas', d: 'Embarcador', i: 'fa-box-open' },
                ] as const
              ).map((o) => (
                <label
                  key={o.v}
                  className={`ifr-escolha ${tipo === o.v ? 'ifr-escolha--ativa' : ''}`}
                >
                  <input
                    type="radio"
                    name="tipo"
                    value={o.v}
                    checked={tipo === o.v}
                    onChange={() => setTipo(o.v)}
                    className="apenas-leitor"
                  />
                  <i
                    className={`fas ${o.i}`}
                    aria-hidden="true"
                  />
                  <span className="ifr-escolha__titulo">{o.t}</span>
                  <span className="ifr-mini ifr-fraco">{o.d}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <Campo
            id="nome"
            rotulo="Nome completo"
            valor={nome}
            aoMudar={setNome}
            erros={campos.name}
            obrigatorio
            autoComplete="name"
          />

          <Campo
            id="email"
            rotulo="E-mail"
            tipo="email"
            valor={email}
            aoMudar={setEmail}
            erros={campos.email}
            obrigatorio
            autoComplete="email"
            inputMode="email"
            dica="Usaremos para confirmar sua conta."
          />

          <Campo
            id="telefone"
            rotulo="Celular"
            tipo="tel"
            valor={telefone}
            aoMudar={(v) => setTelefone(mascararTelefone(v))}
            erros={campos.phone}
            obrigatorio
            autoComplete="tel"
            inputMode="tel"
            placeholder="(11) 98765-4321"
          />

          <div>
            <Campo
              id="senha"
              rotulo="Senha"
              tipo="password"
              valor={senha}
              aoMudar={setSenha}
              erros={campos.password}
              obrigatorio
              autoComplete="new-password"
              dica={`No mínimo ${PASSWORD_MIN_LENGTH} caracteres, com maiúscula, minúscula e número.`}
            />
            {senha && (
              <div className="ifr-mt-p">
                <div
                  className="ifr-forca-barras"
                  aria-hidden="true"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span
                      key={n}
                      className="ifr-forca-barra"
                      // A cor sai do nivel medido: estilo calculado, legitimo.
                      style={{ background: n <= forca.nivel ? forca.barra : 'var(--surface-3)' }}
                    />
                  ))}
                </div>
                {/* aria-live polite: anuncia a mudança de força sem interromper
                    a digitação. */}
                <span
                  aria-live="polite"
                  className="ifr-mini ifr-forte"
                  // A cor vem do nivel medido, entao e calculada: e o caso legitimo
                  // de estilo embutido.
                  style={{ color: forca.cor }}
                >
                  Força da senha: {forca.texto}
                </span>
              </div>
            )}
          </div>

          <div className={`br-checkbox ${campos.acceptedTerms ? 'danger' : ''}`}>
            <input
              id="aceito"
              type="checkbox"
              checked={aceito}
              onChange={(e) => setAceito(e.target.checked)}
              aria-invalid={Boolean(campos.acceptedTerms) || undefined}
              aria-describedby={campos.acceptedTerms ? 'aceito-erro' : undefined}
            />
            {/* Rótulo SEM link dentro, e isso é o ponto.
                A versão anterior era "Li e aceito os [termos de uso] e a
                [política de privacidade]", com os dois como <Link>. Como o
                <label> inteiro é a área de clique do checkbox, o centro do
                texto — o alvo natural — era um <a>: clicar ali NAVEGAVA em vez
                de marcar o aceite, e o formulário preenchido se perdia. Pior,
                as duas rotas não existem, então o destino era a página 404.
                Consentimento e navegação não podem dividir o mesmo alvo. */}
            <label htmlFor="aceito" className="ifr-nota">
              Li e aceito os termos de uso e a política de privacidade do iFretes.
            </label>
            {campos.acceptedTerms && (
              <span id="aceito-erro" className="feedback danger" role="alert">
                {campos.acceptedTerms.join(' ')}
              </span>
            )}
            {/* Fora do <label>: não rouba o clique do aceite. E diz a verdade —
                os documentos ainda não estão publicados, e apontar para uma
                rota inexistente era pedir aceite de um texto ilegível. */}
            <p className="ifr-mini ifr-fraco ifr-mt-p">
              Os documentos completos estão em preparação. Até a publicação, peça a versão
              vigente em{' '}
              <a href="mailto:contato@gplansis.com" className="ifr-elo">
                contato@gplansis.com
              </a>
              .
            </p>
          </div>

          <Botao tipo="submit" carregando={enviando} larguraTotal>
            Criar conta
          </Botao>
        </form>

        <p
          className="ifr-divisor ifr-mt-g ifr-fraco"
        >
          Já tem conta?{' '}
          <Link to="/entrar" className="ifr-elo">
            Entrar
          </Link>
        </p>
      </Cartao>
    </div>
  )
}
